import { useState, useMemo, useRef } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/components/admin/AdminLayout'
import Button from '@/components/ui/Button'
import Notification from '@/components/ui/Notification'
import { FormInput } from '@/components/ui/form'
import { ArrowLeftIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface PermissionRow { id: number; scope: string; action: string }
interface AllPermission { id: number; scope: string; action: string; label: string }

interface RoleData {
  id: number; name: string
  permissions_count: number; users_count: number
  permission_ids: number[]
  permissions: PermissionRow[]
}

interface Props {
  role: RoleData
  all_permissions: AllPermission[]
}

// ─── Hash-based color for badges ────────────────

const BADGE_COLORS = [
  { bg: 'bg-sky-50 dark:bg-sky-500/10', text: 'text-sky-700 dark:text-sky-400', ring: 'ring-sky-200 dark:ring-sky-500/20' },
  { bg: 'bg-indigo-50 dark:bg-indigo-500/10', text: 'text-indigo-700 dark:text-indigo-400', ring: 'ring-indigo-200 dark:ring-indigo-500/20' },
  { bg: 'bg-violet-50 dark:bg-violet-500/10', text: 'text-violet-700 dark:text-violet-400', ring: 'ring-violet-200 dark:ring-violet-500/20' },
  { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', ring: 'ring-emerald-200 dark:ring-emerald-500/20' },
  { bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400', ring: 'ring-amber-200 dark:ring-amber-500/20' },
  { bg: 'bg-rose-50 dark:bg-rose-500/10', text: 'text-rose-700 dark:text-rose-400', ring: 'ring-rose-200 dark:ring-rose-500/20' },
  { bg: 'bg-cyan-50 dark:bg-cyan-500/10', text: 'text-cyan-700 dark:text-cyan-400', ring: 'ring-cyan-200 dark:ring-cyan-500/20' },
  { bg: 'bg-orange-50 dark:bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400', ring: 'ring-orange-200 dark:ring-orange-500/20' },
  { bg: 'bg-teal-50 dark:bg-teal-500/10', text: 'text-teal-700 dark:text-teal-400', ring: 'ring-teal-200 dark:ring-teal-500/20' },
  { bg: 'bg-pink-50 dark:bg-pink-500/10', text: 'text-pink-700 dark:text-pink-400', ring: 'ring-pink-200 dark:ring-pink-500/20' },
  { bg: 'bg-lime-50 dark:bg-lime-500/10', text: 'text-lime-700 dark:text-lime-400', ring: 'ring-lime-200 dark:ring-lime-500/20' },
  { bg: 'bg-fuchsia-50 dark:bg-fuchsia-500/10', text: 'text-fuchsia-700 dark:text-fuchsia-400', ring: 'ring-fuchsia-200 dark:ring-fuchsia-500/20' },
]

function hashColor(str: string) {
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
  return BADGE_COLORS[Math.abs(hash) % BADGE_COLORS.length]
}

function ColorBadge({ text }: { text: string }) {
  const c = hashColor(text)
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${c.bg} ${c.text} ${c.ring}`}>
      {text}
    </span>
  )
}

// ─── Searchable Select ──────────────────────────

function SearchSelect({ options, onSelect, placeholder }: {
  options: { value: string; label: string }[]
  onSelect: (value: string) => void
  placeholder?: string
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || 'Search...'}
        className="block w-full rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 dark:bg-zinc-800 dark:ring-white/10">
          {filtered.map(o => (
            <li
              key={o.value}
              onClick={() => { onSelect(o.value); setQuery(''); setOpen(false) }}
              className="cursor-pointer px-3 py-1.5 text-gray-900 hover:bg-indigo-50 hover:text-indigo-600 dark:text-zinc-200 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-400"
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
      {open && filtered.length === 0 && query && (
        <div className="absolute z-20 mt-1 w-full rounded-md bg-white px-3 py-2 text-sm text-gray-400 shadow-lg ring-1 ring-black/5 dark:bg-zinc-800 dark:ring-white/10">
          No matching permissions
        </div>
      )}
      {open && <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />}
    </div>
  )
}

// ─── Component ──────────────────────────────────

export default function RolesShow({ role, all_permissions }: Props) {
  const [formName, setFormName] = useState(role.name)
  const [permissions, setPermissions] = useState<PermissionRow[]>(role.permissions)
  const [showAssignRow, setShowAssignRow] = useState(false)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)

  const usersCount = role.users_count

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  const notify = (msg: string) => { setNotifMessage(msg); setShowNotif(true) }

  const availablePermissions = useMemo(() => {
    const assignedIds = new Set(permissions.map(p => p.id))
    return all_permissions
      .filter(p => !assignedIds.has(p.id))
      .map(p => ({ value: String(p.id), label: `${p.scope}:${p.action}` }))
  }, [all_permissions, permissions])

  const handleSave = async () => {
    if (!formName.trim()) { setFormErrors(['Name is required']); return }
    setFormErrors([]); setSaving(true)
    try {
      const ids = permissions.map(p => p.id)
      const res = await fetch(`/admin/roles/${role.id}.json`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ role: { name: formName.trim(), permission_ids: ids } }),
      })
      if (res.ok) notify('success:Saved:Role updated successfully')
      else { const err = await res.json().catch(() => ({})); setFormErrors(err.errors || ['Failed to save']) }
    } catch { setFormErrors(['Network error']) }
    setSaving(false)
  }

  const removePermission = (id: number) => {
    setPermissions(prev => prev.filter(p => p.id !== id))
  }

  const assignPermission = (value: string) => {
    const perm = all_permissions.find(p => p.id === Number(value))
    if (!perm) return
    setPermissions(prev =>
      [...prev, { id: perm.id, scope: perm.scope, action: perm.action }]
        .sort((a, b) => `${a.scope}:${a.action}`.localeCompare(`${b.scope}:${b.action}`))
    )
    setShowAssignRow(false)
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this role?')) return
    try {
      const res = await fetch(`/admin/roles/${role.id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) router.visit('/admin/roles')
    } catch { /* silent */ }
  }

  return (
    <>
      <Head title={`Edit ${role.name}`} />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />
      <AdminLayout>
      <main>
        <header className="border-b border-gray-200 dark:border-zinc-700">
          <div className="flex items-start justify-between py-4 px-4 sm:px-6 lg:px-8">
            <div>
              <Link href="/admin/roles" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200">
                <ArrowLeftIcon className="h-4 w-4" /> Back to Roles
              </Link>
              <h1 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">{role.name}</h1>
              <Link href={`/admin/users?role_id=${role.id}`} className="text-sm text-indigo-600 hover:text-indigo-500 hover:underline dark:text-indigo-400">
                {usersCount} user{usersCount !== 1 ? 's' : ''}
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/admin/roles"><Button variant="secondary">Cancel</Button></Link>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</Button>
            </div>
          </div>
        </header>

        {formErrors.length > 0 && (
          <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-200 dark:divide-zinc-700">
          {/* Role Name */}
          <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Role Details</h2>
              <p className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">Configure the role name.</p>
            </div>
            <div className="md:col-span-2">
              <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
                <div className="sm:col-span-4">
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">Name</label>
                  <div className="mt-2">
                    <FormInput value={formName} onChange={e => setFormName((e.target as HTMLInputElement).value)} placeholder="Role name..." />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions Table */}
          <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Assigned Permissions</h2>
              <p className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">
                {permissions.length} permission{permissions.length !== 1 ? 's' : ''} assigned.
              </p>
            </div>
            <div className="md:col-span-2">
              {/* Assign button + search row at top */}
              <div className="mb-3">
                {showAssignRow ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SearchSelect
                        options={availablePermissions}
                        onSelect={assignPermission}
                        placeholder="Search permission to assign..."
                      />
                    </div>
                    <button onClick={() => setShowAssignRow(false)} className="rounded p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <Button size="sm" variant="secondary" onClick={() => setShowAssignRow(true)}>
                    <PlusIcon className="mr-1 h-4 w-4" /> Assign Permission
                  </Button>
                )}
              </div>

              <div className="overflow-hidden rounded-lg ring-1 ring-gray-200 dark:ring-zinc-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                  <thead className="bg-gray-50 dark:bg-zinc-800/50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-zinc-400">Scope</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-zinc-400">Action</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white dark:divide-zinc-800 dark:bg-zinc-900">
                    {permissions.map(p => (
                      <tr key={p.id} className="group">
                        <td className="px-3 py-1.5"><ColorBadge text={p.scope} /></td>
                        <td className="px-3 py-1.5"><ColorBadge text={p.action} /></td>
                        <td className="px-3 py-1.5 text-right">
                          <button
                            onClick={() => removePermission(p.id)}
                            className="rounded p-0.5 text-zinc-300 opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-600 dark:text-zinc-600 dark:hover:text-red-400"
                            title="Remove"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {permissions.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-3 py-6 text-center text-sm text-gray-400 dark:text-zinc-500">No permissions assigned</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Delete */}
          <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Delete Role</h2>
              <p className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">Permanently remove this role. This action cannot be undone.</p>
            </div>
            <div className="flex items-start md:col-span-2">
              <button onClick={handleDelete} className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500">
                Delete role
              </button>
            </div>
          </div>
        </div>
      </main>
      </AdminLayout>
    </>
  )
}

RolesShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
